import { useMemo, useState } from 'react';
import { type ApiRequestError, useApiCreate, useContractLaborCategoriesQuery } from '@access-cms/api-client';
import { CSV_FORMULA_REJECTION_MESSAGE, hasSpreadsheetFormulaPrefix } from '@access-cms/shared-constants';

import { Button, ButtonVariation } from '../../../components/Button/Button';
import Dropdown from '../../../components/Dropdown/Dropdown';
import RadioGroup from '../../../components/Radio/RadioGroup';
import { RequiredFieldsLegend } from '../../../components/RequiredFieldsLegend';
import TextField from '../../../components/TextField/TextField';
import { ValidationAnnouncement } from '../../../components/ValidationAnnouncement';
import { useValidationAnnouncement } from '../../../hooks/useValidationAnnouncement';
import './AddRosterUserModal.css';

const ROSTER_SYSTEM_ERROR_MESSAGE =
  'We are sorry. The Roster System is unable to process your update at this time. Please try again later.';

function getErrorMessage(code?: string, defaultMessage?: string): string {
  const errorMessages: Record<string, string> = {
    '400.EM.ROSTER.1001': 'Only contractor users can be added to the roster.',
    '400.EM.ROSTER.1002': 'Invalid labor category for this contract.',
    '400.EM.ROSTER.1003': 'This user is already assigned to this contract.',
    '400.EM.ROSTER.1004': 'A pending invitation already exists for this email.',
    '400.EM.ROSTER.1005': 'Invalid labor category for this contract.',
    '400.EM.ROSTER.1011': 'The selected labor category is inactive.',
    '400.EM.ROSTER.1012': 'Please enter a valid email address.',
    '400.EM.ROSTER.1013': 'Legal First Name is required.',
    '400.EM.ROSTER.1014': 'Legal Last Name is required.',
    '400.EM.ROSTER.1015': 'Labor Category is required',
  };

  return code && errorMessages[code] ? errorMessages[code] : defaultMessage || 'Failed to add user to roster.';
}

function isApiRequestError(error: unknown): error is ApiRequestError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message?: unknown }).message === 'string' &&
    'statusCode' in error &&
    typeof (error as { statusCode?: unknown }).statusCode === 'number' &&
    (!('name' in error) ||
      (error as { name?: unknown }).name === undefined ||
      (error as { name?: unknown }).name === 'ApiRequestError') &&
    (!('errors' in error) || Array.isArray((error as { errors?: unknown }).errors)) &&
    (!('code' in error) ||
      typeof (error as { code?: unknown }).code === 'string' ||
      (error as { code?: unknown }).code === undefined)
  );
}

function getValidationErrorMessage(error: ApiRequestError): string {
  const fieldError = error.errors?.find((entry) => entry.message);

  return getErrorMessage(fieldError?.code ?? error.code, fieldError?.message ?? error.message);
}

function getRequestErrorMessage(error: unknown): string {
  if (isApiRequestError(error)) {
    return error.message || ROSTER_SYSTEM_ERROR_MESSAGE;
  }

  if (error instanceof Error) {
    return ROSTER_SYSTEM_ERROR_MESSAGE;
  }

  return ROSTER_SYSTEM_ERROR_MESSAGE;
}

interface AddRosterUserModalProps {
  contractId: string;
  contractNumber?: string;
  /**
   * ACP-2438: the contract setting that governs whether anyone on this contract may be
   * designated as working six months or less. When the contract does not permit temporary
   * or seasonal users the question is not asked at all and the answer is recorded as No,
   * so the form cannot produce a designation the contract forbids.
   */
  hasTemporarySeasonalUsers?: boolean;
  onCancel: () => void;
  onRequestError: (message: string) => void;
  onSuccess: (userData: AddRosterUserData) => void;
}

export interface AddRosterUserData {
  firstName: string;
  lastName: string;
  email: string;
  laborCategoryId: string;
  isSeasonal: boolean;
}

const SHORT_TERM_OPTIONS = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

function AddRosterUserModal({
  contractId,
  contractNumber,
  hasTemporarySeasonalUsers,
  onCancel,
  onRequestError,
  onSuccess,
}: AddRosterUserModalProps) {
  const baseTestId = 'add-roster-user-modal';
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [laborCategoryId, setLaborCategoryId] = useState<string>('');
  const allowsTemporarySeasonal = hasTemporarySeasonalUsers === true;
  // Not asked means answered No: the value is still submitted, so the request never
  // relies on a server-side default to land on the safe answer.
  const [isSeasonal, setIsSeasonal] = useState<boolean | undefined>(allowsTemporarySeasonal ? undefined : false);

  const [errors, setErrors] = useState<Partial<Record<keyof AddRosterUserData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  /**
   * ACP-2092 (Section 508): Add User used to set the error state and stop. The field
   * errors rendered, but nothing announced them, so a screen reader user heard silence
   * and had no way to know the press had been rejected — the modal stayed open, looking
   * to them exactly as it had before.
   *
   * Scoped to the modal: an unscoped lookup would sweep in invalid controls on the page
   * behind it and announce fields the user cannot reach.
   */
  const fieldErrorSignature = Object.entries(errors)
    .filter(([, message]) => Boolean(message))
    .map(([field]) => field)
    .join('|');

  const {
    announcement: errorAnnouncement,
    reportInvalidFields,
    scopeRef,
  } = useValidationAnnouncement<HTMLDivElement>({ errorSignature: fieldErrorSignature });

  const laborCategoriesQuery = useContractLaborCategoriesQuery(contractId, {
    enabled: !!contractId,
  });

  const addRosterUserBulk = useApiCreate({
    parentId: contractId || undefined,
    path: '/api/v1/contracts/{contractId}/roster/bulk',
    pathParams: { contractId: contractId || '' },
  });

  // Transform labor categories to dropdown options (only active ones)
  const laborCategoryOptions = useMemo(() => {
    const categories = laborCategoriesQuery.data?.data ?? [];

    return categories
      .filter((cat) => cat.isActive !== false)
      .sort((a, b) => {
        // Sort by displayOrder if available, otherwise by name
        if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
          return a.displayOrder - b.displayOrder;
        }

        return (a.categoryName ?? '').localeCompare(b.categoryName ?? '');
      })
      .map((cat) => ({
        label: cat.categoryName ?? '',
        value: cat.id ?? '',
      }));
  }, [laborCategoriesQuery.data?.data]);

  const isLoadingLaborCategories = laborCategoriesQuery.isLoading;

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof AddRosterUserData, string>> = {};

    if (!firstName.trim()) {
      newErrors.firstName = 'Legal First Name is required';
    } else if (hasSpreadsheetFormulaPrefix(firstName.trim())) {
      newErrors.firstName = CSV_FORMULA_REJECTION_MESSAGE;
    }

    if (!lastName.trim()) {
      newErrors.lastName = 'Legal Last Name is required';
    } else if (hasSpreadsheetFormulaPrefix(lastName.trim())) {
      newErrors.lastName = CSV_FORMULA_REJECTION_MESSAGE;
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!laborCategoryId) {
      newErrors.laborCategoryId = 'Labor Category is required';
    }

    if (isSeasonal === undefined) {
      newErrors.isSeasonal = 'Please select an option';
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  /**
   * ACP-2077 (Section 508): Add User used to be `disabled` until every field was filled,
   * which is the same deadlock the application workflow's Next button had — the control
   * that explains what is missing was inert exactly when something was missing, so a
   * screen reader user met a form they could neither complete nor get an error from.
   *
   * The button is always actionable now and this guard reports instead of submitting.
   */
  const handleConfirm = () => {
    if (!validateForm()) {
      // Batched with the setErrors inside validateForm, so the effect sees the controls
      // already marked invalid.
      reportInvalidFields();

      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    const userData = {
      email: email.trim().toLowerCase(),
      firstName: firstName.trim(),
      isSeasonal: isSeasonal as boolean,
      laborCategoryId,
      lastName: lastName.trim(),
    };

    const bulkPayload = {
      members: [
        {
          email: userData.email,
          firstName: userData.firstName,
          isSeasonal: userData.isSeasonal,
          laborCategoryId: userData.laborCategoryId,
          lastName: userData.lastName,
        },
      ],
    };

    addRosterUserBulk
      .mutateAsync(bulkPayload)
      .then((result) => {
        const failed = result.data?.failed ?? [];
        if (failed.length > 0) {
          const errorInfo = failed[0]?.error;
          setSubmitError(getErrorMessage(errorInfo?.code, errorInfo?.message));
          setIsSubmitting(false);

          return;
        }

        setIsSubmitting(false);
        onSuccess(userData);
      })
      .catch((error: unknown) => {
        console.error('Failed to add user to roster:', error);

        if (isApiRequestError(error) && error.statusCode === 400) {
          setSubmitError(getValidationErrorMessage(error));
          setIsSubmitting(false);

          return;
        }

        setIsSubmitting(false);
        onRequestError(getRequestErrorMessage(error));
      });
  };

  const displayContractNumber = contractNumber ?? contractId;

  return (
    <div
      ref={scopeRef}
      className='add-roster-user-modal'
      data-testid={baseTestId}>
      <ValidationAnnouncement
        data-testid={`${baseTestId}-error-announcement`}
        message={errorAnnouncement}
      />
      <div
        className='add-roster-user-modal__header'
        data-testid={`${baseTestId}-header`}>
        <h2
          className='add-roster-user-modal__title'
          data-testid={`${baseTestId}-title`}>
          Add a user to the Roster.
        </h2>
        <p
          className='add-roster-user-modal__description'
          data-testid={`${baseTestId}-description`}>
          Fill out the required fields to add a user to the Roster for Contract {displayContractNumber}.
        </p>
        <RequiredFieldsLegend data-testid={`${baseTestId}-required-fields`} />
      </div>
      {submitError && (
        <div
          className='add-roster-user-modal__error-alert'
          data-testid={`${baseTestId}-error`}
          role='alert'>
          {submitError}
        </div>
      )}
      <div
        className='add-roster-user-modal__form'
        data-testid={`${baseTestId}-form`}>
        <TextField
          required
          data-testid={`${baseTestId}-first-name`}
          disabled={isSubmitting}
          errorMessage={errors.firstName}
          label='Legal First Name'
          name='firstName'
          placeholder='- Enter Legal First Name -'
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
            if (errors.firstName) {
              setErrors((prev) => ({ ...prev, firstName: undefined }));
            }
          }}
        />
        <TextField
          required
          data-testid={`${baseTestId}-last-name`}
          disabled={isSubmitting}
          errorMessage={errors.lastName}
          label='Legal Last Name'
          name='lastName'
          placeholder='- Enter Legal Last Name -'
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value);
            if (errors.lastName) {
              setErrors((prev) => ({ ...prev, lastName: undefined }));
            }
          }}
        />
        <TextField
          required
          data-testid={`${baseTestId}-email`}
          disabled={isSubmitting}
          errorMessage={errors.email}
          label='Email'
          name='email'
          placeholder='- Enter Email Address -'
          type='email'
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (errors.email) {
              setErrors((prev) => ({ ...prev, email: undefined }));
            }
          }}
        />
        <Dropdown
          required
          data-testid={`${baseTestId}-labor-category`}
          defaultValue={laborCategoryId}
          disabled={isSubmitting || isLoadingLaborCategories}
          errorMessage={errors.laborCategoryId}
          hint={isLoadingLaborCategories ? 'Loading labor categories...' : undefined}
          label='Labor Category'
          name='laborCategoryId'
          options={laborCategoryOptions}
          onChange={(value) => {
            setLaborCategoryId(value);
            if (errors.laborCategoryId) {
              setErrors((prev) => ({ ...prev, laborCategoryId: undefined }));
            }
          }}
        />
        {allowsTemporarySeasonal && (
          <div
            className='add-roster-user-modal__short-term-section'
            data-testid={`${baseTestId}-short-term`}>
            <RadioGroup<boolean>
              required
              data-testid={`${baseTestId}-short-term-radio`}
              errorMessage={errors.isSeasonal}
              label='Will this User be working on this Contract for six (6) months or less?'
              name='isSeasonal'
              options={SHORT_TERM_OPTIONS.map((opt) => ({ ...opt, disabled: isSubmitting }))}
              value={isSeasonal}
              onChange={(value) => {
                setIsSeasonal(value);
                if (errors.isSeasonal) {
                  setErrors((prev) => ({ ...prev, isSeasonal: undefined }));
                }
              }}
            />
            <p
              className='add-roster-user-modal__short-term-hint'
              data-testid={`${baseTestId}-short-term-hint`}>
              By selecting Yes, you acknowledge that this person will be working on this contract for six (6) months or
              less and that they may need to reapply.
            </p>
          </div>
        )}
      </div>
      <div
        className='add-roster-user-modal__actions'
        data-testid={`${baseTestId}-actions`}>
        <Button
          isAutoWidth
          data-testid={`${baseTestId}-cancel`}
          disabled={isSubmitting}
          variation={ButtonVariation.SOLID}
          onClick={onCancel}>
          Cancel
        </Button>
        <Button
          isAutoWidth
          data-testid={`${baseTestId}-submit`}
          disabled={isSubmitting || isLoadingLaborCategories}
          variation={ButtonVariation.SOLID}
          onClick={handleConfirm}>
          {isSubmitting ? 'Adding...' : 'Add User'}
        </Button>
      </div>
    </div>
  );
}

export default AddRosterUserModal;
